import type { CSSProperties } from 'react';

import { cn } from '../../styles/lib/utils';

/**
 * Sanitizes a column ID for use in CSS variable names.
 * Replaces dots and other invalid CSS identifier characters with hyphens.
 * CSS custom properties must be valid CSS identifiers, and dots are not valid.
 *
 * @example
 * toCssVarName('requestor.name') // 'requestor-name'
 * toCssVarName('user.email') // 'user-email'
 * toCssVarName('simple-id') // 'simple-id'
 */
export function toCssVarName(columnId: string): string {
  return columnId.replace(/\./g, '-').replace(/#/g, '--');
}

export type PinnedCellLayout = {
  side: 'left' | 'right';
  /** CSS length for the sticky `left` / `right`, built from the column size vars so it tracks resizing. */
  offset: string;
  /** Last left-pinned or first right-pinned column, which draws the border against the scrolling columns. */
  isEdge: boolean;
  /** Edge shadow, skipped when the selection checkbox is the only left-pinned column. */
  hasShadow: boolean;
};

/** CSS length of the summed column size vars of `columnIds`. */
export function sumSizeVars(columnIds: string[]): string {
  if (columnIds.length === 0) return '0px';
  return `calc((${columnIds.map((id) => `var(--col-${toCssVarName(id)}-size)`).join(' + ')}) * 1px)`;
}

/** Sticky layout of each visible pinned column, keyed by column id; both lists are in visual order. */
export function getPinnedCellLayouts(left: string[], right: string[]): Map<string, PinnedCellLayout> {
  const layouts = new Map<string, PinnedCellLayout>();
  const leftHasShadow = left.length > 1 || (left.length === 1 && left[0] !== 'select');
  left.forEach((id, index) => {
    layouts.set(id, {
      side: 'left',
      offset: sumSizeVars(left.slice(0, index)),
      isEdge: index === left.length - 1,
      hasShadow: leftHasShadow,
    });
  });
  right.forEach((id, index) => {
    layouts.set(id, {
      side: 'right',
      offset: sumSizeVars(right.slice(index + 1)),
      isEdge: index === 0,
      hasShadow: true,
    });
  });
  return layouts;
}

type Band = { left: number; right: number };

/** Screen extent of the left- and right-pinned header cells inside a table's scroll container. */
export function readPinnedBands(sections: Element): { left?: Band; right?: Band } {
  const bandOf = (side: 'left' | 'right'): Band | undefined => {
    const cells = sections.querySelectorAll(`[data-slot="grid-header"] [role="columnheader"][data-pinned="${side}"]`);
    let band: Band | undefined;
    for (const cell of cells) {
      if (cell.closest('[data-slot="table-sections"]') !== sections) continue;
      const rect = cell.getBoundingClientRect();
      band = band
        ? { left: Math.min(band.left, rect.left), right: Math.max(band.right, rect.right) }
        : { left: rect.left, right: rect.right };
    }
    return band;
  };
  return { left: bandOf('left'), right: bandOf('right') };
}

/** Props that hold a pinned cell in place while the table scrolls sideways; empty for an unpinned cell. */
export function getPinnedCellProps(
  layout: PinnedCellLayout | undefined,
  { isHeader = false }: { isHeader?: boolean } = {},
): { className?: string; style?: CSSProperties; 'data-pinned'?: 'left' | 'right' } {
  if (!layout) return {};
  const { side, offset, isEdge, hasShadow } = layout;
  return {
    'data-pinned': side,
    className: cn(
      // Header cells sit in the sticky header's own stacking context, so they only need to clear the scrolling headers.
      isHeader ? 'z-1' : 'z-2',
      isEdge && side === 'left' && 'border-grey-30 border-r',
      isEdge && side === 'right' && 'border-grey-30 border-l',
      isEdge &&
        hasShadow &&
        side === 'left' &&
        "after:pointer-events-none after:absolute after:top-0 after:right-[-5px] after:bottom-0 after:w-1 after:shadow-[inset_4px_0px_4px_-4px_gray] after:content-['']",
      isEdge &&
        hasShadow &&
        side === 'right' &&
        "before:pointer-events-none before:absolute before:top-0 before:bottom-0 before:left-[-5px] before:w-1 before:shadow-[inset_-4px_0px_4px_-4px_gray] before:content-['']",
    ),
    style: { position: 'sticky', [side]: offset },
  };
}

/**
 * Recursively flattens a nested column structure into a flat array of leaf columns.
 * Leaf columns are those that do not have a `columns` property.
 *
 * @param columns - Array of column objects that may contain nested `columns` arrays
 * @returns Flat array of all leaf column objects (columns without nested `columns`)
 *
 * @example
 * const nestedColumns = [
 *   {
 *     id: "#0",
 *     header: "",
 *     columns: [
 *       { id: "id", header: "ID", accessorKey: "id" },
 *       { id: "name", header: "Name", accessorKey: "name" }
 *     ]
 *   },
 *   {
 *     id: "requestor",
 *     header: "Requestor",
 *     columns: [
 *       { id: "requestor.name", header: "Name", accessorKey: "requestor.name" }
 *     ]
 *   }
 * ];
 * const flat = flattenColumns(nestedColumns);
 * // Returns: [
 * //   { id: "id", header: "ID", accessorKey: "id" },
 * //   { id: "name", header: "Name", accessorKey: "name" },
 * //   { id: "requestor.name", header: "Name", accessorKey: "requestor.name" }
 * // ]
 */
export function flattenColumns<T extends { columns?: T[] }>(columns: T[]): T[] {
  const result: T[] = [];

  for (const column of columns) {
    if (column.columns && Array.isArray(column.columns)) {
      // Recursively flatten nested columns
      result.push(...flattenColumns(column.columns));
    } else {
      // This is a leaf node (no columns property)
      result.push(column);
    }
  }

  return result;
}

/**
 * Zebra striping alternates by top-level row position (raw index breaks once
 * expanded sub-rows interleave with their parents); sub-rows share their parent's stripe.
 */
export function getRowStripeColors(rows: ReadonlyArray<{ depth: number }>): Array<'white' | 'grey'> {
  let topLevelIndex = -1;
  return rows.map((row) => {
    if (row.depth === 0) topLevelIndex += 1;
    return topLevelIndex % 2 === 0 ? 'white' : 'grey';
  });
}
